import { useEffect, useRef } from 'react';

type Props = {
  /** Whether the infinite scroll is enabled */
  isEnabled?: boolean;
  /** Whether there is more content to load */
  hasMore?: boolean;
  /** Callback to trigger when reaching bottom */
  onScrollToBottom?: () => void;
  /** Distance from bottom in pixels to trigger */
  bottomOffset?: number;
};

const useWindowInfiniteScroll = ({
  isEnabled,
  hasMore,
  onScrollToBottom,
  bottomOffset = 20,
}: Props) => {
  const rafIdRef = useRef<number | null>(null);
  const hasMoreRef = useRef(hasMore);
  const isEnabledRef = useRef(isEnabled);

  // Keep refs in sync to avoid stale closures
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  useEffect(() => {
    // SSR compatibility check
    if (typeof window === 'undefined') {
      return;
    }

    const handleScroll = () => {
      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Use requestAnimationFrame for performance
      rafIdRef.current = requestAnimationFrame(() => {
        // Check if we should process this scroll event
        if (!isEnabledRef.current || !hasMoreRef.current) {
          return;
        }

        // Calculate scroll position
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || window.pageYOffset;
        const clientHeight = window.innerHeight;

        // Check if we're within bottomOffset pixels of the bottom
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const isNearBottom = distanceFromBottom < bottomOffset;

        if (isNearBottom) {
          onScrollToBottom?.();
        }
      });
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [onScrollToBottom, bottomOffset]);
};

export default useWindowInfiniteScroll;
